import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Field } from "@/components/common";
import { KeyRound, Home, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { tapi, setTenantToken } from "@/lib/tenantApi";

export default function TenantPortalLogin() {
  const nav = useNavigate();
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // First-login reset state
  const [resetToken, setResetToken] = useState("");
  const [resetTenantName, setResetTenantName] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const login = async (e) => {
    e?.preventDefault?.();
    if (!ident.trim() || !password) return toast.error("Enter your phone / email / tenant code and password");
    setBusy(true);
    try {
      const r = await tapi.login(ident.trim(), password);
      if (r.must_reset_password) {
        setResetToken(r.reset_token);
        setResetTenantName(r.tenant?.name || "");
        setNewPw(""); setConfirmPw("");
        toast.info("Please set a new password to continue.");
      } else {
        setTenantToken(r.token);
        toast.success(`Welcome, ${r.tenant.name}`);
        nav("/portal", { replace: true });
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const submitNewPw = async (e) => {
    e?.preventDefault?.();
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    if (newPw === password) return toast.error("Pick a new password different from the one your landlord shared");
    setBusy(true);
    try {
      const r = await tapi.changePasswordWithReset(resetToken, newPw);
      setTenantToken(r.token);
      toast.success("Password updated. Welcome!");
      nav("/portal", { replace: true });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not set new password"); }
    finally { setBusy(false); }
  };

  const cancelReset = () => { setResetToken(""); setNewPw(""); setConfirmPw(""); setPassword(""); };

  return (
    <div className="min-h-screen flex" data-testid="tenant-portal-login">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-950 text-slate-50 p-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-emerald-500 flex items-center justify-center"><Home className="w-5 h-5" /></div>
          <span className="font-heading font-bold text-xl">Tenant Portal</span>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-bold tracking-tight leading-tight">Your rent.<br/>Your account.<br/><span className="text-emerald-400">One place.</span></h1>
          <p className="text-slate-400 mt-6 max-w-md">Sign in to view your invoices, download receipts, check your security deposit and pay outstanding rent — securely.</p>
        </div>
        <p className="text-slate-500 text-xs">Powered by RentLedger · <Link to="/login" className="underline hover:text-slate-300">Landlord sign-in →</Link></p>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md p-8 border-slate-200 shadow-sm">
          <div className="lg:hidden flex items-center gap-2 mb-4"><div className="w-9 h-9 rounded-md bg-emerald-500 flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div><span className="font-heading font-bold text-xl">Tenant Portal</span></div>

          {!resetToken ? (
            <>
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><KeyRound className="w-6 h-6 text-emerald-600" /> Sign in</h2>
              <p className="text-slate-500 text-sm mt-1 mb-5">Access your rental account and pay outstanding invoices.</p>
              <form onSubmit={login} className="space-y-4">
                <Field label="Phone Number, Email or Tenant Code">
                  <Input data-testid="pw-identifier" value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="9864000000 · you@example.com · TEN-0001" autoFocus />
                </Field>
                <Field label="Password">
                  <Input data-testid="pw-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </Field>
                <Button data-testid="pw-login-btn" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </Button>
                <p className="text-xs text-slate-500 text-center">Don't have a password yet? Ask your landlord to set one for you.</p>
              </form>
            </>
          ) : (
            <div data-testid="first-login-reset">
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-emerald-600" /> Set a new password</h2>
              <p className="text-slate-500 text-sm mt-1 mb-5">
                Welcome{resetTenantName ? `, ${resetTenantName}` : ""}. For your security, please replace the temporary password your landlord shared with something only you know.
              </p>
              <form onSubmit={submitNewPw} className="space-y-4">
                <Field label="New Password (min 6 characters — letters, digits or a mix)">
                  <Input data-testid="new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" autoFocus />
                </Field>
                <Field label="Confirm New Password">
                  <Input data-testid="confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat the same password" />
                </Field>
                <Button data-testid="submit-new-password-btn" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Sign In"}
                </Button>
                <button type="button" onClick={cancelReset} className="text-xs text-slate-500 underline w-full text-center" data-testid="cancel-reset-btn">Sign in as someone else</button>
              </form>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
